import React, { useState } from "react";
import { DocumentWithVersionInfo } from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Download, MoreHorizontal, File, FileImage, FileSpreadsheet, Trash2, History, FilePlus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import DocumentUploader from "./document-uploader";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import UploadNewVersionDialog from './upload-new-version-dialog';
import VersionHistoryDialog from './version-history-dialog';

interface DocumentListProps {
  documents: DocumentWithVersionInfo[];
  entityId: string;
  entityType: "claim" | "contract";
  hideUploadButton?: boolean;
}

export default function DocumentList({ documents, entityId, entityType, hideUploadButton = false }: DocumentListProps) {
  const { toast } = useToast();
  const [isUploadNewVersionDialogOpen, setIsUploadNewVersionDialogOpen] = useState(false);
  const [selectedDocumentForVersioning, setSelectedDocumentForVersioning] = useState<DocumentWithVersionInfo | null>(null);
  const [isVersionHistoryDialogOpen, setIsVersionHistoryDialogOpen] = useState(false);
  const [selectedDocumentForHistory, setSelectedDocumentForHistory] = useState<DocumentWithVersionInfo | null>(null);
  
  // Get file icon based on file type
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (extension === 'pdf') {
      return <FileText className="h-10 w-10 text-red-500" />;
    } else if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension || '')) {
      return <FileImage className="h-10 w-10 text-blue-500" />;
    } else if (['xls', 'xlsx', 'csv'].includes(extension || '')) {
      return <FileSpreadsheet className="h-10 w-10 text-green-600" />;
    } else {
      return <File className="h-10 w-10 text-neutral-500" />;
    }
  };
  
  // Download document
  const handleDownload = async (documentId: string, fileName: string) => {
    try {
      // First fetch the download URL from the API with proper authentication
      const response = await apiRequest("GET", `/api/documents/${documentId}/download`);
      const data = await response.json();
      
      // Open the signed URL in a new tab
      window.open(data.downloadUrl, '_blank');
      
      toast({
        title: "Download started",
        description: `Downloading ${fileName}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download document",
        variant: "destructive",
      });
    }
  };

  // Delete document
  const handleDelete = async (documentId: string, fileName: string) => {
    if (!confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiRequest("DELETE", `/api/documents/${documentId}`);
      
      toast({
        title: "Document deleted",
        description: `${fileName} has been deleted successfully.`,
      });
      
      // Invalidate queries to refresh the document list
      if (entityType === "claim") {
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${entityId}`] });
      } else {
        queryClient.invalidateQueries({ queryKey: [`/api/contracts/${entityId}`] });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const handleOpenUploadNewVersionDialog = (doc: DocumentWithVersionInfo) => {
    setSelectedDocumentForVersioning(doc);
    setIsUploadNewVersionDialogOpen(true);
  };

  const handleOpenVersionHistoryDialog = (doc: DocumentWithVersionInfo) => {
    setSelectedDocumentForHistory(doc);
    setIsVersionHistoryDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Documents</h3>
        {!hideUploadButton && (
          <DocumentUploader entityType={entityType} entityId={entityId} />
        )}
      </div>
      
      {documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <Card key={doc.documentMetadataId} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 flex items-start gap-4">
                  {getFileIcon(doc.fileName)}
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{doc.fileName}</h4>
                    <p className="text-sm text-muted-foreground">{doc.documentType}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDownload(doc.documentMetadataId, doc.fileName)}>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleOpenUploadNewVersionDialog(doc)}
                        >
                          <FilePlus className="mr-2 h-4 w-4" />
                          Upload New Version
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleOpenVersionHistoryDialog(doc)}
                        >
                          <History className="mr-2 h-4 w-4" />
                          Version History
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(doc.documentMetadataId, doc.fileName)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                <div className="border-t p-2 bg-muted/50 flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleDownload(doc.documentMetadataId, doc.fileName)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(doc.documentMetadataId, doc.fileName)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-10 text-center">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-neutral-500">No documents uploaded yet.</p>
            {!hideUploadButton && (
              <DocumentUploader entityType={entityType} entityId={entityId} />
            )}
          </CardContent>
        </Card>
      )}

      {selectedDocumentForVersioning && (
        <UploadNewVersionDialog
          isOpen={isUploadNewVersionDialogOpen}
          onClose={() => {
            setIsUploadNewVersionDialogOpen(false);
            setSelectedDocumentForVersioning(null);
          }}
          document={selectedDocumentForVersioning}
          onSuccess={() => {
            toast({ title: "New version uploaded successfully!" });
            queryClient.invalidateQueries({ queryKey: ['documents', entityId] }); 
            queryClient.invalidateQueries({ queryKey: ['projectDocuments', entityId] });
            queryClient.invalidateQueries({ queryKey: ['claimDocuments', entityId] });
            setIsUploadNewVersionDialogOpen(false);
            setSelectedDocumentForVersioning(null);
          }}
        />
      )}

      {selectedDocumentForHistory && (
        <VersionHistoryDialog
          isOpen={isVersionHistoryDialogOpen}
          onClose={() => {
            setIsVersionHistoryDialogOpen(false);
            setSelectedDocumentForHistory(null);
          }}
          document={selectedDocumentForHistory}
        />
      )}
    </div>
  );
}
